import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, CheckCircle, XCircle, Award, Calendar, Star, Clock } from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ValidateCertificatePage = () => {
  const { code } = useParams();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [result, setResult] = useState(null);
  const [searchCode, setSearchCode] = useState(code || '');

  useEffect(() => {
    if (code) {
      validateCertificate(code);
    } else {
      setLoading(false);
    }
  }, [code]);

  const validateCertificate = async (certCode) => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/exams/certificates/validate/${certCode}`);
      setResult(response.data);
    } catch (err) {
      setResult({ valid: false, message: 'Erro ao validar certificado' });
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchCode.trim()) {
      navigate(`/validar/${searchCode.trim()}`);
      validateCertificate(searchCode.trim());
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: 'long',
      year: 'numeric'
    });
  };

  return (
    <div className="min-h-screen bg-black pt-24 pb-12">
      <div className="container mx-auto px-4 max-w-2xl">
        {/* Header */}
        <div className="mb-8">
          <Button
            onClick={() => navigate('/')}
            variant="ghost"
            className="text-white hover:text-red-500 mb-4"
          >
            <ArrowLeft className="mr-2" size={20} />
            Voltar ao Início
          </Button>
          
          <div className="text-center">
            <Award className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h1 className="text-3xl font-bold text-white mb-2">Validar Certificado</h1>
            <p className="text-gray-400">
              Verifique a autenticidade de um certificado da AJ BIM Academy
            </p>
          </div>
        </div>

        {/* Search Form */}
        <form onSubmit={handleSearch} className="mb-8">
          <div className="flex gap-2">
            <input
              type="text"
              value={searchCode}
              onChange={(e) => setSearchCode(e.target.value.toUpperCase())}
              placeholder="Digite o código do certificado (ex: AJBIM-001-ABC123)"
              className="flex-1 bg-zinc-900 border border-zinc-700 rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-red-500"
            />
            <Button type="submit" className="bg-red-600 hover:bg-red-700 px-6">
              Validar
            </Button>
          </div>
        </form>

        {/* Result */}
        {loading ? (
          <Card className="bg-zinc-900 border-zinc-800">
            <CardContent className="py-12 text-center">
              <div className="w-12 h-12 border-2 border-red-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
              <p className="text-gray-400">Validando certificado...</p>
            </CardContent>
          </Card>
        ) : result ? (
          result.valid ? (
            <Card className="bg-green-900/20 border-green-700">
              <CardContent className="py-8">
                <div className="text-center mb-6">
                  <CheckCircle className="w-20 h-20 text-green-500 mx-auto mb-4" />
                  <h2 className="text-2xl font-bold text-green-400 mb-2">
                    Certificado Válido
                  </h2>
                  <p className="text-gray-400">
                    Este certificado é autêntico e foi emitido pela AJ BIM Academy.
                  </p>
                </div>

                <div className="bg-zinc-900/50 rounded-lg p-6 space-y-4">
                  <div className="flex justify-between items-center border-b border-zinc-700 pb-3">
                    <span className="text-gray-400">Aluno(a)</span>
                    <span className="text-white font-semibold">{result.certificate.user_name}</span>
                  </div>
                  
                  <div className="flex justify-between items-center border-b border-zinc-700 pb-3">
                    <span className="text-gray-400">Curso</span>
                    <span className="text-white font-semibold">{result.certificate.course_title}</span>
                  </div>
                  
                  <div className="flex justify-between items-center border-b border-zinc-700 pb-3">
                    <span className="text-gray-400 flex items-center gap-1">
                      <Clock size={14} />
                      Carga Horária
                    </span>
                    <span className="text-white">{result.certificate.course_hours}h</span>
                  </div>
                  
                  <div className="flex justify-between items-center border-b border-zinc-700 pb-3">
                    <span className="text-gray-400 flex items-center gap-1">
                      <Star size={14} />
                      Nota Final
                    </span>
                    <span className="text-white">{result.certificate.score.toFixed(1)}</span>
                  </div>
                  
                  <div className="flex justify-between items-center border-b border-zinc-700 pb-3">
                    <span className="text-gray-400 flex items-center gap-1">
                      <Calendar size={14} />
                      Data de Emissão
                    </span>
                    <span className="text-white">{formatDate(result.certificate.issued_at)}</span>
                  </div>
                  
                  <div className="flex justify-between items-center">
                    <span className="text-gray-400">Código</span>
                    <span className="text-yellow-500 font-mono">{result.certificate.certificate_code}</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          ) : (
            <Card className="bg-red-900/20 border-red-700">
              <CardContent className="py-12 text-center">
                <XCircle className="w-20 h-20 text-red-500 mx-auto mb-4" />
                <h2 className="text-2xl font-bold text-red-400 mb-2">
                  Certificado Inválido
                </h2>
                <p className="text-gray-400">
                  {result.message || 'Não encontramos um certificado com este código.'}
                </p>
                <p className="text-gray-500 text-sm mt-4">
                  Verifique se o código foi digitado corretamente.
                </p>
              </CardContent>
            </Card>
          )
        ) : (
          <Card className="bg-zinc-900 border-zinc-800">
            <CardContent className="py-12 text-center">
              <Award className="w-16 h-16 text-gray-600 mx-auto mb-4" />
              <p className="text-gray-400">
                Digite o código do certificado para verificar sua autenticidade.
              </p>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default ValidateCertificatePage;
